<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'dashboard/components-next/icon/Icon.vue';

// Only reads counts that are already loaded into the board's column state —
// no extra requests, no new data source.
const props = defineProps({
  columns: { type: Object, required: true },
});

const emit = defineEmits(['selectStage']);

const { t } = useI18n();

const OVERVIEW_STAGES = [
  {
    stage: 'New',
    labelKey: 'KANBAN.OVERVIEW.NEW',
    icon: 'i-lucide-sparkles',
    iconClass: 'bg-n-blue-3 text-n-blue-11',
  },
  {
    stage: 'Qualified',
    labelKey: 'KANBAN.OVERVIEW.QUALIFIED',
    icon: 'i-lucide-badge-check',
    iconClass: 'bg-n-teal-3 text-n-teal-11',
  },
  {
    stage: 'Meeting Scheduled',
    labelKey: 'KANBAN.OVERVIEW.MEETING',
    icon: 'i-lucide-calendar',
    iconClass: 'bg-n-iris-3 text-n-iris-11',
  },
  {
    stage: 'Proposal Sent',
    labelKey: 'KANBAN.OVERVIEW.PROPOSAL',
    icon: 'i-lucide-file-text',
    iconClass: 'bg-n-amber-3 text-n-amber-11',
  },
  {
    stage: 'Won',
    labelKey: 'KANBAN.OVERVIEW.WON',
    icon: 'i-lucide-trophy',
    iconClass: 'bg-n-teal-3 text-n-teal-11',
  },
  {
    stage: 'Lost',
    labelKey: 'KANBAN.OVERVIEW.LOST',
    icon: 'i-lucide-circle-x',
    iconClass: 'bg-n-slate-3 text-n-slate-11',
  },
];

const stats = computed(() =>
  OVERVIEW_STAGES.map(item => ({
    ...item,
    label: t(item.labelKey),
    count: props.columns[item.stage]?.count ?? 0,
  }))
);
</script>

<template>
  <div
    class="grid flex-shrink-0 grid-cols-2 mb-3 overflow-hidden border rounded-xl sm:grid-cols-3 lg:grid-cols-6 border-n-weak bg-n-solid-1"
  >
    <button
      v-for="stat in stats"
      :key="stat.stage"
      type="button"
      class="flex items-center gap-3 px-[18px] py-4 text-left transition-colors border-r last:border-r-0 border-n-weak hover:bg-n-alpha-1"
      @click="emit('selectStage', stat.stage)"
    >
      <div
        class="flex items-center justify-center flex-shrink-0 rounded-xl size-[38px]"
        :class="stat.iconClass"
      >
        <Icon :icon="stat.icon" class="size-[18px]" />
      </div>
      <div class="flex flex-col min-w-0">
        <span class="text-xs font-medium truncate text-n-slate-10">
          {{ stat.label }}
        </span>
        <span
          class="text-2xl font-bold leading-tight text-n-slate-12 tabular-nums"
        >
          {{ stat.count }}
        </span>
      </div>
    </button>
  </div>
</template>
